/**
 * Number Formatting Utility
 *
 * This module provides a standardized way to format different types of numeric values
 * across the application, including token amounts, prices, fiat values, and percentages.
 * It uses the native Intl.NumberFormat API instead of third-party libraries for better
 * performance and long-term maintainability.
 *
 * The formatting logic is based on a declarative rule system where each display type
 * has its own set of formatting rules for different value ranges.
 */

import { CurrencyAmount, Currency, Price } from '@uniswap/sdk-core';
import { type Maybe } from '@/types';
import { Bound } from '@/utils/types';

/**
 * Available display types for number formatting.
 * Each type has its own formatting rules defined in FORMAT_CONFIGS.
 */
export enum DisplayType {
  /** For token quantities (e.g., "1,234.56 ETH") */
  TokenAmount = 'token-amount',

  /** For token-to-token price ratios (e.g., "0.034561 ETH/DAI") */
  TokenPrice = 'token-price',

  /** For very small token prices with subscript zeros (e.g., "0.0₄126261") */
  TokenPriceCompact = 'token-price-compact',

  /** For full fiat value displays with dollar sign (e.g., "$1,234.56") */
  Fiat = 'fiat',

  /** For abbreviated fiat values (e.g., "$1.23k") */
  FiatShort = 'fiat-short',

  /** For percentage values with % sign (e.g., "12.34%") */
  Percentage = 'percentage',
}

/**
 * Defines a single formatting rule with conditions and output format specification.
 */
export type FormatRule = {
  /** Upper bound for this rule to apply (inclusive) */
  upperBound?: number;

  /** Exact value match for this rule to apply */
  exact?: number;

  /** The format pattern to use (similar to numeral.js patterns) */
  format: string;

  /** Optional prefix to add before the formatted number */
  prefix?: string;

  /** Optional suffix to add after the formatted number */
  suffix?: string;

  /** For special cases where we want to format a different value than the input */
  hardCodedInput?: { input: number };
};

/**
 * A collection of formatting rules for a specific display type.
 */
export type FormatConfig = {
  rules: FormatRule[];
};

/**
 * Formats a number with subscript notation for leading zeros after decimal point.
 * For example, 0.0000126261 will be formatted as "0.0₄126261"
 *
 * @param value - The numeric value to format
 * @returns Formatted string with subscript notation
 */
function formatSubscriptZero(value: number): string {
  if (value === 0) return '0';

  // For very small values, use subscript notation
  const valueStr = value.toString();

  // Check if in scientific notation
  if (valueStr.includes('e-')) {
    const parts = valueStr.split('e-');
    const base = parseFloat(parts[0]);
    const exponent = parseInt(parts[1], 10);

    // Format as 0.0ₙdigits
    const significantDigits = base.toString().replace('.', '');
    const zeroCount = exponent - 1;

    // Use unicode subscripts for zero count
    const subscriptDigits = String(zeroCount)
      .split('')
      .map((digit) => {
        // Convert digits to unicode subscript characters
        const subscriptChars = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
        return subscriptChars[parseInt(digit, 10)];
      })
      .join('');

    return `0.0${subscriptDigits}${significantDigits}`;
  }

  // Handle regular decimal notation
  const decimalParts = valueStr.split('.');
  if (decimalParts.length !== 2) return valueStr;

  const decimalPart = decimalParts[1];
  // Count leading zeros
  let leadingZeros = 0;
  for (const char of decimalPart) {
    if (char === '0') {
      leadingZeros++;
    } else {
      break;
    }
  }

  // Only apply subscript if we have leading zeros
  if (leadingZeros > 0) {
    // Use unicode subscripts for zero count
    const subscriptDigits = String(leadingZeros)
      .split('')
      .map((digit) => {
        // Convert digits to unicode subscript characters
        const subscriptChars = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
        return subscriptChars[parseInt(digit, 10)];
      })
      .join('');

    const significantPart = decimalPart.substring(leadingZeros);
    return `0.0${subscriptDigits}${significantPart}`;
  }

  // Fallback to standard formatting
  // technically this will not happen as we should set the upper bound to 0.01
  return value.toFixed(6);
}

/**
 * Format configurations with rules for different value ranges.
 * Each display type has its own set of rules that are evaluated in order.
 */
const FORMAT_CONFIGS: Record<DisplayType, FormatConfig> = {
  [DisplayType.TokenAmount]: {
    rules: [
      { exact: 0, format: '0' },
      {
        upperBound: 0.000001,
        format: '0.000000',
        prefix: '<',
        hardCodedInput: { input: 0.000001 },
      },
      { upperBound: 1, format: '0.000000' },
      { upperBound: 1000000, format: '0,0.00' },
      { format: '0.00a' },
    ],
  },
  // token price in terms of another token
  [DisplayType.TokenPrice]: {
    rules: [
      { exact: 0, format: '0' },
      {
        upperBound: 0.000001,
        format: '0.000000',
        prefix: '<',
        hardCodedInput: { input: 0.000001 },
      },
      { upperBound: 10, format: '0.000000' },
      { upperBound: 1000000, format: '0,0.00' },
      { format: '0.00a' },
    ],
  },
  // Compact format for very small token prices using subscript for zeros
  [DisplayType.TokenPriceCompact]: {
    rules: [
      { exact: 0, format: '0' },
      {
        upperBound: 0.01,
        format: 'SUBSCRIPT_ZERO',
      },
      { upperBound: 10, format: '0.000000' },
      { upperBound: 1000000, format: '0,0.00' },
      { format: '0.00a' },
    ],
  },
  [DisplayType.Fiat]: {
    rules: [
      { exact: 0, format: '$0.00' },
      {
        upperBound: 0.000001,
        format: '$0.[000000]',
        prefix: '<',
        hardCodedInput: { input: 0.000001 },
      },
      { upperBound: 1, format: '$0.000000' },
      { upperBound: 1e6, format: '$0,0.00' },
      { upperBound: 1e16, format: '$0.00a' },
      { format: '$0.00e+0' },
    ],
  },
  [DisplayType.FiatShort]: {
    rules: [
      { exact: 0, format: '$0' },
      {
        upperBound: 0.01,
        format: '$0.[00]',
        prefix: '<',
        hardCodedInput: { input: 0.01 },
      },
      { upperBound: 1, format: '$0.00' },
      { upperBound: 1e6, format: '$0,0.00' },
      { upperBound: 1e16, format: '$0.00a' },
      { format: '$0.00e+0' },
    ],
  },
  [DisplayType.Percentage]: {
    rules: [
      { exact: 0, format: '0%' },
      {
        upperBound: 0.0001,
        format: '0.00%',
        prefix: '<',
        hardCodedInput: { input: 0.0001 },
      },
      { upperBound: 100, format: '0.00%' },
      { format: '0%' },
    ],
  },
};

/**
 * Converts a format string (similar to numeral.js patterns) into options for Intl.NumberFormat.
 * This function parses format strings like "0,0.00", "$0.00", "0.00%", etc.
 *
 * @param formatStr - The format string to parse
 * @returns Configuration object for Intl.NumberFormat with optional prefix/suffix
 */
function parseFormatString(
  formatStr: string,
): Intl.NumberFormatOptions & { prefix?: string; suffix?: string } {
  const options: Intl.NumberFormatOptions & { prefix?: string; suffix?: string } = {
    useGrouping: formatStr.includes(','),
  };

  // Extract decimal places
  let maxDecimals = 0;
  let minDecimals = 0;

  if (formatStr.includes('.')) {
    const decimalPart = formatStr.split('.')[1];
    if (decimalPart) {
      // Handle optional decimals like [00]
      const optionalMatch = decimalPart.match(/\[([0-9]+)\]/);
      if (optionalMatch) {
        maxDecimals = optionalMatch[1].length;
        // If we have optional decimals, min is 0
        minDecimals = 0;
      } else {
        // Count fixed decimal places by counting zeros after the decimal point
        const fixedDecimals = (decimalPart.match(/0+/)?.[0] ?? '').length;
        minDecimals = fixedDecimals;
        maxDecimals = fixedDecimals;
      }
    }
  }

  options.minimumFractionDigits = minDecimals;
  options.maximumFractionDigits = maxDecimals;

  // Handle currency symbol
  if (formatStr.includes('$')) {
    options.style = 'currency';
    options.currency = 'USD';
    // Remove $ from the format as it will be added by Intl
    options.prefix = '';
  }

  // Handle percentage
  if (formatStr.includes('%')) {
    options.style = 'percent';
    // Percent symbol will be added automatically
    options.suffix = '';
  }

  // Handle abbreviations (k, M, B, etc.)
  if (formatStr.includes('a')) {
    options.notation = 'compact';
    options.compactDisplay = 'short';
  }

  return options;
}

/**
 * Formats large numbers with appropriate abbreviations (k, m, b, t).
 * This function provides consistent abbreviation formatting across the app.
 *
 * @param num - The number to format
 * @param decimals - Number of decimal places to include (default: 2)
 * @returns Formatted string with appropriate abbreviation
 */
function formatWithAbbreviations(num: number, decimals = 2): string {
  if (num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum < 1000) {
    return sign + absNum.toFixed(decimals).replace(/\.?0+$/, '');
  }

  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: decimals,
  });

  // Make sure abbreviations are lowercase to match tests
  return sign + formatter.format(absNum).toLowerCase();
}

/**
 * Finds the appropriate formatting rule for a given value from a rule set.
 * Rules are evaluated in order, checking for exact matches first, then ranges.
 *
 * @param value - The numeric value to find a rule for
 * @param rules - Array of format rules to check against
 * @returns The matching format rule
 */
function findFormatRule(value: number, rules: FormatRule[]): FormatRule {
  // Handle exact matches first - use strict equality for zero
  const exactMatch = rules.find((rule) => rule.exact !== undefined && value === rule.exact);
  if (exactMatch) return exactMatch;

  // Find the first rule where value is less than upperBound
  const rangeMatch = rules.find(
    (rule) =>
      rule.exact === undefined && (rule.upperBound === undefined || value < rule.upperBound),
  );
  if (rangeMatch) return rangeMatch;

  // Fallback to last rule
  return rules[rules.length - 1];
}

export type FormatNumberOptions = {
  /** The value to format: number, string, or CurrencyAmount */
  value: Maybe<number | string | CurrencyAmount<Currency>>;

  /** The display type that determines formatting rules */
  type: DisplayType;

  /** Optional custom format configuration to override default rules */
  customConfig?: Partial<FormatRule>;
};

/**
 * Formats a number according to specified display type and custom configuration.
 * This is the main formatting function that should be used throughout the application.
 *
 * @param options - Formatting options including value, type and custom config
 * @returns Formatted string according to the specified rules
 *
 * @example
 * ```ts
 * // Token amount: "1,234.56"
 * format({ value: 1234.56, type: DisplayType.TokenAmount })
 *
 * // Token amount from string: "1,234.56"
 * format({ value: "1234.56", type: DisplayType.TokenAmount })
 *
 * // Fiat: "$1,234.56"
 * format({ value: 1234.56, type: DisplayType.Fiat })
 *
 * // Percentage: "12.34%"
 * format({ value: 0.1234, type: DisplayType.Percentage })
 *
 * // Compact price format with subscript zeros: "0.0₄126261"
 * format({ value: 0.0000126261, type: DisplayType.TokenPriceCompact })
 *
 * // Custom: "~1,234.56 ETH"
 * format({
 *   value: 1234.56,
 *   type: DisplayType.TokenAmount,
 *   customConfig: { prefix: '~', suffix: ' ETH' }
 * })
 *
 * // Small values with threshold: "<0.01"
 * format({ value: 0.001, type: DisplayType.FiatShort })
 * ```
 */
export function format({ value, type, customConfig = {} }: FormatNumberOptions): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // Convert value to number
  let numericValue: number;
  if (value instanceof CurrencyAmount) {
    numericValue = parseFloat(value.toSignificant());
  } else if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
      return '-';
    }
    numericValue = parsed;
  } else {
    numericValue = value;
  }

  // Remove special handling for TokenPriceCompact

  const config = FORMAT_CONFIGS[type];
  const rule = findFormatRule(numericValue, config.rules);
  const finalRule = { ...rule, ...customConfig };

  let result = '';

  // Determine the actual number we'll format (may be different from numericValue in special cases)
  const formatValue = finalRule.hardCodedInput ? finalRule.hardCodedInput.input : numericValue;

  // Handle special format for subscript zeros
  if (finalRule.format === 'SUBSCRIPT_ZERO') {
    return formatSubscriptZero(formatValue);
  } else if (finalRule.format.includes('a')) {
    // Handle abbreviations
    const decimals = (finalRule.format.match(/0\.([0]+)a/) ?? ['', ''])[1].length;
    result = formatWithAbbreviations(formatValue, decimals);
  } else {
    // Parse formatting options for Intl.NumberFormat
    const formatOptions = parseFormatString(finalRule.format);

    // Handle percentages
    let valueToFormat = formatValue;
    if (formatOptions.style === 'percent') {
      // Don't need to multiply by 100 as Intl.NumberFormat does this automatically
    }

    const formatter = new Intl.NumberFormat('en-US', formatOptions);
    result = formatter.format(valueToFormat);

    // Remove automatically added currency symbol if we're handling it with prefix
    if (formatOptions.style === 'currency') {
      result = result.replace(/^\$/, '');
    }

    // Remove automatically added percent symbol if we're handling it with suffix
    if (formatOptions.style === 'percent') {
      result = result.replace(/%$/, '');
    }
  }

  // Apply prefix and suffix from the rule
  const prefix = finalRule.prefix ?? '';
  const suffix = finalRule.suffix ?? '';

  // Add back currency symbol or percent if needed
  let finalPrefix = prefix;
  let finalSuffix = suffix;

  if (finalRule.format.includes('$') && !prefix.includes('$')) {
    // Special case: handle < prefix with $ correctly
    finalPrefix = prefix === '<' ? '<$' : '$' + prefix;
  }

  if (finalRule.format.includes('%') && !suffix.includes('%')) {
    finalSuffix = finalSuffix + '%';
  }

  return `${finalPrefix}${result}${finalSuffix}`;
}

/**
 * Convenience function for compact number formatting with uppercase abbreviations.
 * Useful for displaying large numbers in a space-constrained UI.
 *
 * @param value - Number to format compactly
 * @returns Formatted string with uppercase abbreviation (e.g., "1.2K", "3.4M")
 */
export function formatCompact(value: number): string {
  // Keep uppercase for the formatCompact function specifically
  return formatWithAbbreviations(value, 1).toUpperCase();
}

export type FormatTickPriceOptions = {
  /** Price value to format */
  price?: { toSignificant: () => string } | string;

  /** Object indicating if we're at the limit for a given bound */
  atLimit: { [bound in Bound]?: boolean | undefined };

  /** Which direction/bound we're formatting */
  direction: Bound;
};

/**
 * Format price for a tick with special handling for price range bounds.
 * Used specifically for rendering pool position ranges.
 *
 * @param options - Format options including price and bound information
 * @returns Formatted string with special handling for range limits
 *
 * @example
 * // At lower bound (returns "0")
 * formatTickPrice({
 *   price: "0.1",
 *   atLimit: { [Bound.LOWER]: true },
 *   direction: Bound.LOWER
 * })
 *
 * // At upper bound (returns "∞")
 * formatTickPrice({
 *   price: "1000",
 *   atLimit: { [Bound.UPPER]: true },
 *   direction: Bound.UPPER
 * })
 *
 * // Normal price (returns formatted price)
 * formatTickPrice({
 *   price: "123.45",
 *   atLimit: {},
 *   direction: Bound.LOWER
 * })
 */
export function formatTickPrice({ price, atLimit, direction }: FormatTickPriceOptions): string {
  if (atLimit[direction]) {
    return direction === Bound.LOWER ? '0' : '∞';
  }

  if (!price) {
    return '-';
  }

  const rv = format({
    value: typeof price === 'string' ? price : parseFloat(price.toSignificant()),
    type: DisplayType.TokenPrice,
  });

  return rv;
}

/**
 * Format a Price object from the SDK.
 * Useful for displaying exchange rates between tokens.
 *
 * @param price - The Price object to format
 * @returns Formatted price string
 *
 * @example
 * // Returns formatted price like "1,234.56"
 * formatCurrencyPrice({
 *   price: new Price(token0, token1, denominator, numerator)
 * })
 */
export function formatCurrencyPrice({
  price,
  useCompact = false,
}: {
  price: Price<Currency, Currency>;
  useCompact?: boolean;
}): string {
  return format({
    value: parseFloat(price.toSignificant()),
    type: useCompact ? DisplayType.TokenPriceCompact : DisplayType.TokenPrice,
  });
}

/**
 * Format a price value that can be either a string or number.
 *
 * @param options.price - The price value to format (string or number)
 * @param options.useCompact - Whether to use the compact display format with subscript zeros
 * @returns Formatted price string
 */
export function formatCurrencyPriceValue({
  price,
  useCompact = false,
}: {
  price: string | number;
  useCompact?: boolean;
}): string {
  return format({
    value: price,
    type: useCompact ? DisplayType.TokenPriceCompact : DisplayType.TokenPrice,
  });
}

/**
 * @deprecated Use formatCurrencyPriceValue instead
 */
export function formatCurrencyPriceStr({
  priceStr,
  useCompact = false,
}: {
  priceStr: string;
  useCompact?: boolean;
}): string {
  return formatCurrencyPriceValue({ price: priceStr, useCompact });
}
